/**
 * trackConversion utility for GA4 generate_lead event
 * Fires exactly once per unique rid using sessionStorage dedupe
 * Supports both gtag and GTM dataLayer
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const CONVERSION_KEY_PREFIX = 'vgfs_generate_lead_fired_';
const IS_DEV = process.env.NODE_ENV === 'development';

interface ConversionParams {
  rid?: string;
  loan_amount?: number;
  product?: string;
  currency?: string;
  value?: number;
}

/**
 * Check if conversion has already been fired for this rid
 */
function hasConversionFired(rid: string): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    const key = `${CONVERSION_KEY_PREFIX}${rid}`;
    return sessionStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

/**
 * Mark conversion as fired for this rid
 */
function markConversionFired(rid: string): void {
  if (typeof window === 'undefined') return;
  
  try {
    const key = `${CONVERSION_KEY_PREFIX}${rid}`;
    sessionStorage.setItem(key, 'true');
  } catch (error) {
    console.warn('[trackConversion] Failed to mark conversion:', error);
  }
}

/**
 * Fire GA4 generate_lead event with dedupe protection
 * Returns true if event was fired, false if skipped (already fired or error)
 */
export function trackGenerateLead(params: ConversionParams): boolean {
  if (typeof window === 'undefined') return false;
  
  const rid = params.rid || 'no_rid';
  
  if (hasConversionFired(rid)) {
    if (IS_DEV) {
      console.log('[trackConversion] Skipped - already fired for rid:', rid);
    }
    return false;
  }
  
  const eventParams = {
    currency: params.currency || 'AUD',
    value: params.value ?? 1,
    loan_amount: params.loan_amount,
    product: params.product || 'chattel_mortgage',
    rid: params.rid,
  };
  
  let success = false;
  
  try {
    if (window.gtag) {
      window.gtag('event', 'generate_lead', eventParams);
      success = true;
      
      if (IS_DEV) {
        console.log('[trackConversion] generate_lead fired via gtag:', eventParams);
      }
    } else if (window.dataLayer) {
      window.dataLayer.push({
        event: 'generate_lead',
        ...eventParams,
      });
      success = true;
      
      if (IS_DEV) {
        console.log('[trackConversion] generate_lead fired via dataLayer:', eventParams);
      }
    } else {
      if (IS_DEV) {
        console.warn('[trackConversion] Neither gtag nor dataLayer available');
      }
    }
    
    if (success) {
      markConversionFired(rid);
    }
  } catch (error) {
    console.error('[trackConversion] Error firing generate_lead:', error);
    return false;
  }
  
  return success;
}

/**
 * Fire Google Ads conversion event (separate from GA4 generate_lead)
 */
export function trackGoogleAdsConversion(params: ConversionParams & { 
  send_to?: string;
}): boolean {
  if (typeof window === 'undefined' || !window.gtag) return false;
  
  const rid = params.rid || 'no_rid';
  const adsKey = `vgfs_ads_conversion_fired_${rid}`;
  
  try {
    if (sessionStorage.getItem(adsKey) === 'true') {
      if (IS_DEV) {
        console.log('[trackConversion] Google Ads conversion skipped - already fired for rid:', rid);
      }
      return false;
    }
    
    const sendTo = params.send_to || 'AW-16985904042/AmDzCM7N4ZMbEKqnwaM_';
    
    window.gtag('event', 'conversion', {
      send_to: sendTo,
      value: params.value ?? 1.0,
      currency: params.currency || 'AUD',
      transaction_id: rid,
    });
    
    sessionStorage.setItem(adsKey, 'true');
    
    if (IS_DEV) {
      console.log('[trackConversion] Google Ads conversion fired:', { send_to: sendTo, rid });
    }
    
    return true;
  } catch (error) {
    console.error('[trackConversion] Error firing Google Ads conversion:', error);
    return false;
  }
}

/**
 * Combined function to fire both GA4 generate_lead and Google Ads conversion
 */
export function trackLeadConversion(params: ConversionParams): { 
  ga4Fired: boolean; 
  adsFired: boolean;
} {
  const ga4Fired = trackGenerateLead(params);
  const adsFired = trackGoogleAdsConversion(params);
  
  if (IS_DEV) {
    console.log('[trackConversion] Lead conversion result:', { 
      ga4Fired, 
      adsFired, 
      rid: params.rid,
      loan_amount: params.loan_amount 
    });
  }
  
  return { ga4Fired, adsFired };
}

/**
 * Reset conversion state for a specific rid (for testing)
 */
export function resetConversionState(rid: string): void {
  if (typeof window === 'undefined') return;
  
  try {
    sessionStorage.removeItem(`${CONVERSION_KEY_PREFIX}${rid}`);
    sessionStorage.removeItem(`vgfs_ads_conversion_fired_${rid}`);
    
    if (IS_DEV) {
      console.log('[trackConversion] Reset conversion state for rid:', rid);
    }
  } catch (error) {
    console.warn('[trackConversion] Failed to reset conversion state:', error);
  }
}
